import React, {
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useInsertionEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import BaseFormComponent, {
    type RequiredIndicator,
    type RightSideContent,
    useFormFieldIds,
} from './BaseFormComponent';
import CaretDownIcon from './CustomIcons/CaretDownIcon';
import InfoFilledIcon from './CustomIcons/InfoFilledIcon';
import SearchIcon from './CustomIcons/SearchIcon';
import LoadingInput from './LoadingInput';
import Spinner from './Spinner';
import Tag from './Tag';
import Tooltip from './Tooltip';
import { CONTROL_TEXT_SIZE, CONTROL_TEXT_SIZE_CLASS } from './controlTextSize';
import { controlWidthStyle } from './controlWidth';
import { DROPDOWN_TRIGGER_ATTR, getTriggerVisual } from './dropdownTriggerStyle';
import { pushEscapeLayer } from './escapeLayers';
import {
    MENU_MAX_HEIGHT_PX,
    MENU_SPECS,
    type MenuGeometry,
    measureMenuGeometry,
} from './floatingMenuGeometry';
import { ensureMultiSelectPanelStyles } from './optionRowStyles';
import { overlayIdentity } from './portalOverlay';
import { useTopLayer } from './topLayer';

const EMPTY_MESSAGE_STYLE: React.CSSProperties = {
    padding: '8px 10px',
    color: 'var(--malachite-text-secondary, rgb(123, 123, 123))',
};

export type MultiSelectSize = 'small' | 'medium' | 'large';

// Mirrors Dropdown, which is what Figma specs a select as.
const TRIGGER_SIZE_HEIGHT: Record<MultiSelectSize, string> = {
    small: 'h-[var(--malachite-control-height-small,28px)]',
    medium: 'h-[var(--malachite-control-height-medium,32px)]',
    large: 'h-[var(--malachite-control-height-large,42px)]',
};

const TRIGGER_SIZE_CLASSES: Record<MultiSelectSize, string> = {
    small: `${TRIGGER_SIZE_HEIGHT.small} gap-[6px] px-2.5 ${CONTROL_TEXT_SIZE_CLASS.small} rounded-[var(--malachite-radius-10,10px)]`,
    medium: `${TRIGGER_SIZE_HEIGHT.medium} gap-[8px] px-3 ${CONTROL_TEXT_SIZE_CLASS.medium} rounded-xl`,
    large: `${TRIGGER_SIZE_HEIGHT.large} gap-[10px] px-4 ${CONTROL_TEXT_SIZE_CLASS.large} rounded-[var(--malachite-radius-xxl,14px)]`,
};

export interface MultiSelectOption {
    value: string;
    label: React.ReactNode;
    searchText?: string;
    /** Prevent toggling or removing this option — used for mandatory/locked selections */
    disabled?: boolean;
    /** Right-aligned badge shown in the dropdown only (not in chips) */
    badge?: React.ReactNode;
    /** Description text for this option */
    description?: string;
    /** How description renders — 'icon': info icon with tooltip, 'subtitle': text below label, 'hover': tooltip on label. Default: 'icon' */
    descriptionMode?: 'icon' | 'subtitle' | 'hover';
    /** Custom icon for 'icon' mode. Default: built-in ⓘ */
    descriptionIcon?: React.ReactNode;
    /** Truncate subtitle after N lines. Only applies to 'subtitle' mode */
    descriptionMaxLines?: number;
}

interface MultiSelectBaseProps {
    options: MultiSelectOption[];
    /** Controlled value. When provided, component is fully controlled. */
    value?: string[];
    /** Initial value for uncontrolled usage (like Dropdown's defaultValue). */
    defaultValue?: string[];
    onChange?: (next: string[]) => void;
    placeholder?: string;
    renderTrigger?: (selected: MultiSelectOption[]) => React.ReactNode;
    searchable?: boolean;
    /** 'large' (42px) for form inputs; 'medium' (32px) for toolbar chips; 'small' (28px) beside a title. */
    size?: MultiSelectSize;
    width?: string | number;
    minWidth?: number;
    disabled?: boolean;
    error?: boolean;
    loading?: boolean;
    label?: string;
    description?: string;
    tooltip?: string;
    hint?: string;
    rightSideContent?: RightSideContent;
    required?: boolean;
    requiredIndicator?: RequiredIndicator;
    /** Show selected values as chips instead of "N selected" text */
    chips?: boolean;
    /** Show × button on each chip to remove it (only when chips=true) */
    chipsRemovable?: boolean;
    /** Max visible chips. 'auto' = show all chips. Number = hard limit with "+N" badge */
    chipsMax?: number | 'auto';
    /** Search input inside the trigger field instead of external search bar */
    inlineSearch?: boolean;
    /**
     * Called with the query as typed, and with `''` whenever the component clears it — on
     * select, and on close — so a consumer's request stays in step with what is on screen.
     *
     * For a list too large to load in full: fire the request here and pass the results back
     * as `options`. Pair it with `localFilter={false}`, or the component filters the answer
     * the server already filtered. Debouncing is left to the caller, who is the only one who
     * knows what a request costs.
     */
    onSearchChange?: (query: string) => void;
    /**
     * Whether this component filters `options` itself. Default true.
     *
     * Set false when the options already reflect the query. A server commonly matches on
     * fields the label does not contain — an account found by its owner's email, say — and
     * filtering again here would hide precisely those matches.
     */
    localFilter?: boolean;
    className?: string;
    style?: React.CSSProperties;
    /** Names the trigger from the light DOM, where `htmlFor` cannot reach into the shadow root. */
    id?: string;
    'aria-label'?: string;
    'aria-labelledby'?: string;
}

// When formControl is provided, name is required for form registration
export type MultiSelectProps =
    | (MultiSelectBaseProps & {
          formControl: Control<FieldValues, unknown>;
          name: string;
      })
    | (MultiSelectBaseProps & {
          formControl?: undefined;
          name?: string;
      });

const AUTO_SEARCHABLE_THRESHOLD = 10;

const KEY_ARROW_DOWN = 'ArrowDown';
const KEY_ARROW_UP = 'ArrowUp';
const KEY_ENTER = 'Enter';
const KEY_BACKSPACE = 'Backspace';
const NAV_KEYS = new Set([KEY_ARROW_DOWN, KEY_ARROW_UP, KEY_ENTER]);

// Nesting interactive elements inside <button> is invalid HTML —
// combobox for text input (inline search), group for chip buttons, button otherwise
interface TriggerElementProps extends React.HTMLAttributes<HTMLElement> {
    triggerRole: 'combobox' | 'group' | 'button';
    disabled: boolean;
    children: React.ReactNode;
}

const TriggerElement = React.forwardRef<HTMLButtonElement | HTMLDivElement, TriggerElementProps>(
    ({ triggerRole, disabled, children, ...props }, ref) => {
        if (triggerRole !== 'button') {
            return (
                <div
                    ref={ref as React.Ref<HTMLDivElement>}
                    role={triggerRole}
                    tabIndex={disabled ? -1 : 0}
                    {...(props as React.HTMLAttributes<HTMLDivElement>)}
                >
                    {children}
                </div>
            );
        }
        return (
            <button
                ref={ref as React.Ref<HTMLButtonElement>}
                type="button"
                disabled={disabled}
                {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
            >
                {children}
            </button>
        );
    },
);
TriggerElement.displayName = 'TriggerElement';

const MultiSelect: React.FC<MultiSelectProps> = ({
    options,
    value: controlledValue,
    defaultValue,
    onChange,
    placeholder = 'Select',
    renderTrigger,
    searchable,
    size = 'large',
    width = 'auto',
    minWidth,
    disabled = false,
    error = false,
    loading = false,
    label,
    name,
    description,
    tooltip,
    hint,
    rightSideContent,
    required = false,
    requiredIndicator,
    formControl,
    chips = false,
    chipsRemovable = true,
    chipsMax = 'auto',
    inlineSearch = false,
    onSearchChange,
    localFilter = true,
    className = '',
    style,
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
}) => {
    // Stable IDs for aria-activedescendant and aria-controls
    const instanceId = useId();
    const listboxId = `${instanceId}-listbox`;
    const getOptionId = (index: number) => `${instanceId}-option-${index}`;
    const { controlId, labelId, descriptionId, hintId, describedBy } = useFormFieldIds({
        id,
        label,
        description,
        hint,
    });

    // Mount-only initial value — matches native <input defaultValue> semantics
    const [internalValue, setInternalValue] = useState<string[]>(() => defaultValue ?? []);
    const isControlled = controlledValue !== undefined;
    const value = isControlled ? controlledValue : internalValue;

    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState<MenuGeometry | null>(null);
    const [search, setSearch] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const inlineInputRef = useRef<HTMLInputElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    useTopLayer(popoverRef, open);

    const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

    // Set lookup — `value.includes(...)` inside a filter or a per-row render is O(n),
    // making selection checks O(options x value) on every pass.
    const selectedValues = useMemo(() => new Set(value), [value]);

    const selectedOptions = useMemo(
        () => options.filter((o) => selectedValues.has(o.value)),
        [options, selectedValues],
    );

    const maxVisibleChips = typeof chipsMax === 'number' ? chipsMax : undefined;

    const filteredOptions = useMemo(() => {
        // The options already answer the query when the consumer is fetching them.
        if (!localFilter) return options;
        const query = search.trim().toLowerCase();
        if (!query) return options;
        return options.filter((o) => {
            const text = o.searchText ?? (typeof o.label === 'string' ? o.label : '');
            return text.toLowerCase().includes(query);
        });
    }, [options, search, localFilter]);

    // Programmatic search clears (after Enter) should preserve highlight position
    const isUserSearching = useRef(false);

    /**
     * Every change to the query goes through here, including the programmatic clears on
     * select and on close, so `onSearchChange` cannot fall out of step with what the list is
     * showing: a consumer left holding the previous query would keep serving results for a
     * search the component has already forgotten.
     *
     * The ref mirrors the state so a repeated clear does not fire the callback again — the
     * close effect runs on every close, whether or not anything was typed.
     */
    const searchRef = useRef('');
    const updateSearch = useCallback(
        (next: string, fromUser: boolean) => {
            if (searchRef.current === next) return;
            searchRef.current = next;
            if (fromUser) isUserSearching.current = true;
            setSearch(next);
            onSearchChange?.(next);
        },
        [onSearchChange],
    );

    const handleSearchChange = useCallback(
        (newSearch: string) => updateSearch(newSearch, true),
        [updateSearch],
    );

    const clearSearch = useCallback(() => updateSearch('', false), [updateSearch]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: search triggers the effect, ref gates it
    useEffect(() => {
        if (isUserSearching.current) {
            setHighlightedIndex(-1);
            isUserSearching.current = false;
        }
    }, [search]);

    // A consumer fetching its options always needs the input, however few results came
    // back: auto-hiding it on a short list would take the search away mid-typing, exactly
    // when the query has narrowed the list to a handful.
    const isSearchable =
        searchable ?? (onSearchChange !== undefined || options.length > AUTO_SEARCHABLE_THRESHOLD);

    // Inline search replaces the external search bar
    const showSearch = inlineSearch ? false : isSearchable;

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;

        const geometry = measureMenuGeometry(popoverRef.current, {
            triggerRect: trigger.getBoundingClientRect(),
            preset: 'standard',
            minWidth,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
        });

        setPosition(geometry);
    }, [minWidth]);

    useEffect(() => {
        if (!open) {
            clearSearch();
            setHighlightedIndex(-1);
            setPosition(null);
            return;
        }
        updatePosition();
        // Auto-focus inline search so user can type immediately
        if (inlineSearch) inlineInputRef.current?.focus();

        const handleScrollOrResize = () => updatePosition();
        const handleClickOutside = (e: MouseEvent) => {
            const path = e.composedPath();
            const trigger = triggerRef.current;
            const popover = popoverRef.current;
            if ((!trigger || !path.includes(trigger)) && (!popover || !path.includes(popover))) {
                setOpen(false);
            }
        };
        // ESC via the shared layer stack: closes only the topmost overlay.
        const popEscapeLayer = pushEscapeLayer(() => setOpen(false));
        window.addEventListener('scroll', handleScrollOrResize, true);
        window.addEventListener('resize', handleScrollOrResize);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
            document.removeEventListener('mousedown', handleClickOutside);
            popEscapeLayer();
        };
    }, [open, updatePosition, inlineSearch, clearSearch]);

    useLayoutEffect(() => {
        if (!open) {
            return;
        }
        updatePosition();

        const popover = popoverRef.current;
        if (!popover || typeof ResizeObserver === 'undefined') {
            return;
        }
        const observer = new ResizeObserver(() => updatePosition());
        observer.observe(popover);
        return () => observer.disconnect();
    }, [open, updatePosition]);

    const updateValue = useCallback(
        (next: string[]) => {
            if (!isControlled) setInternalValue(next);
            onChange?.(next);
        },
        [isControlled, onChange],
    );

    const isOptionDisabled = useCallback(
        (optionValue: string) => {
            return options.some((o) => o.value === optionValue && o.disabled);
        },
        [options],
    );

    const toggle = useCallback(
        (optionValue: string) => {
            if (isOptionDisabled(optionValue)) return;
            if (value.includes(optionValue)) {
                updateValue(value.filter((v) => v !== optionValue));
            } else {
                updateValue([...value, optionValue]);
            }
        },
        [value, updateValue, isOptionDisabled],
    );

    const disabledValues = useMemo(() => {
        const locked = new Set<string>();
        for (const option of options) {
            if (option.disabled) {
                locked.add(option.value);
            }
        }
        return locked;
    }, [options]);

    const clearAll = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            // Keep disabled (locked) options when clearing
            const locked = value.filter((v) => disabledValues.has(v));
            updateValue(locked);
        },
        [updateValue, value, disabledValues],
    );

    const removeChip = useCallback(
        (optionValue: string, e: React.MouseEvent) => {
            e.stopPropagation();
            if (isOptionDisabled(optionValue)) return;
            updateValue(value.filter((v) => v !== optionValue));
        },
        [value, updateValue, isOptionDisabled],
    );

    // Inline input lives inside trigger button — stopPropagation prevents
    // the keypress from firing twice (input → button bubble)
    const handleOptionNavigation = useCallback(
        (e: React.KeyboardEvent) => {
            if (!NAV_KEYS.has(e.key)) return;

            e.preventDefault();
            e.stopPropagation();

            if (!open) {
                setOpen(true);
                return;
            }

            const optionCount = filteredOptions.length;
            if (optionCount === 0) return;

            // Manual scroll within the list container — scrollIntoView can scroll the page
            const scrollToOption = (index: number) => {
                requestAnimationFrame(() => {
                    const el = optionRefs.current[index];
                    const container = listRef.current;
                    if (!el || !container) return;

                    const elTop = el.offsetTop;
                    const elBottom = elTop + el.offsetHeight;
                    const containerTop = container.scrollTop;
                    const containerBottom = containerTop + container.clientHeight;

                    if (elBottom > containerBottom) {
                        container.scrollTo({
                            top: elBottom - container.clientHeight,
                            behavior: 'smooth',
                        });
                    } else if (elTop < containerTop) {
                        container.scrollTo({ top: elTop, behavior: 'smooth' });
                    }
                });
            };

            const moveHighlight = (direction: 1 | -1) => {
                let nextIndex = highlightedIndex;
                for (let i = 0; i < optionCount; i++) {
                    nextIndex =
                        direction === 1
                            ? nextIndex < optionCount - 1
                                ? nextIndex + 1
                                : 0
                            : nextIndex > 0
                              ? nextIndex - 1
                              : optionCount - 1;
                    if (!filteredOptions[nextIndex]?.disabled) {
                        scrollToOption(nextIndex);
                        setHighlightedIndex(nextIndex);
                        return;
                    }
                }
            };

            const selectHighlighted = () => {
                if (highlightedIndex < 0 || highlightedIndex >= optionCount) {
                    return;
                }
                if (filteredOptions[highlightedIndex]?.disabled) return;
                const selectedValue = filteredOptions[highlightedIndex].value;
                toggle(selectedValue);
                clearSearch();
                // After clearing search, filteredOptions resets to the full list —
                // re-locate the selected option so the highlight stays on it
                const newIndex = options.findIndex((o) => o.value === selectedValue);
                setHighlightedIndex(newIndex >= 0 ? newIndex : -1);
                scrollToOption(newIndex >= 0 ? newIndex : 0);
                requestAnimationFrame(() => inlineInputRef.current?.focus());
            };

            switch (e.key) {
                case KEY_ARROW_DOWN:
                    moveHighlight(1);
                    break;
                case KEY_ARROW_UP:
                    moveHighlight(-1);
                    break;
                case KEY_ENTER:
                    selectHighlighted();
                    break;
            }
        },
        [open, filteredOptions, highlightedIndex, toggle, options, clearSearch],
    );

    const handleSearchKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            // Empty backspace removes last chip for quick corrections
            if (e.key === KEY_BACKSPACE && search === '' && value.length > 0) {
                e.preventDefault();
                updateValue(value.slice(0, -1));
                // Keep focus on input so user stays in the dropdown context
                requestAnimationFrame(() => inlineInputRef.current?.focus());
                return;
            }
            handleOptionNavigation(e);
        },
        [handleOptionNavigation, search, value, updateValue],
    );

    const clearable = selectedOptions.length > 0 && !disabled && !loading;

    const triggerVisual = getTriggerVisual({
        disabled,
        error,
        open,
        loading,
        hasSelection: selectedOptions.length > 0,
    });

    // Inline search input shared between chip mode and empty state
    const inlineSearchInput = useMemo(() => {
        if (!inlineSearch) return null;
        return (
            <input
                ref={inlineInputRef}
                type="text"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder={selectedOptions.length === 0 ? placeholder : ''}
                // Inherits rather than naming a rung: an input does not take the parent's
                // font by default, and the trigger already sets the one its tier calls for.
                className="flex-1 w-full min-w-[40px] border-none outline-none bg-transparent text-[length:inherit] p-0 text-[var(--malachite-text-primary,rgb(10,10,10))]"
                onMouseDown={(e) => {
                    // Prevent trigger blur while keeping dropdown open
                    e.preventDefault();
                }}
            />
        );
    }, [
        inlineSearch,
        search,
        handleSearchChange,
        handleSearchKeyDown,
        selectedOptions.length,
        placeholder,
    ]);

    const triggerLabel = useMemo(() => {
        if (renderTrigger && selectedOptions.length > 0) {
            return renderTrigger(selectedOptions);
        }

        // Chips give users direct visibility and removal of selected values
        if (chips && selectedOptions.length > 0) {
            const visible =
                maxVisibleChips != null
                    ? selectedOptions.slice(0, maxVisibleChips)
                    : selectedOptions;
            const remaining = selectedOptions.length - visible.length;
            return (
                <div
                    className="flex items-center gap-1 overflow-hidden min-w-0 flex-1"
                    style={{ flexWrap: 'nowrap' }}
                >
                    {visible.map((opt) => {
                        const optDisabled = disabledValues.has(opt.value);
                        return (
                            <Tag
                                key={opt.value}
                                value={opt.label}
                                type="default"
                                textTransform="none"
                                onRemove={
                                    chipsRemovable && !disabled && !optDisabled
                                        ? (e) => removeChip(opt.value, e)
                                        : undefined
                                }
                                className="shrink-0"
                            />
                        );
                    })}
                    {remaining > 0 && (
                        <span className="shrink-0 text-xs text-[var(--malachite-text-secondary,rgb(123,123,123))]">
                            +{remaining}
                        </span>
                    )}
                    {inlineSearchInput}
                </div>
            );
        }

        if (inlineSearch && selectedOptions.length === 0) {
            return inlineSearchInput;
        }

        if (selectedOptions.length === 0) {
            return <span className="text-[var(--malachite-text-placeholder)]">{placeholder}</span>;
        }
        if (selectedOptions.length === 1) {
            return <span>{selectedOptions[0].label}</span>;
        }
        return <span>{`${selectedOptions.length} selected`}</span>;
    }, [
        selectedOptions,
        renderTrigger,
        chips,
        maxVisibleChips,
        chipsRemovable,
        disabled,
        removeChip,
        inlineSearch,
        inlineSearchInput,
        placeholder,
        disabledValues,
    ]);

    const emptyMessage = options.length === 0 ? 'No options available' : 'No results found';

    useInsertionEffect(() => {
        if (open) {
            ensureMultiSelectPanelStyles();
        }
    }, [open]);

    const popoverStyle: React.CSSProperties = {
        position: 'fixed',
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        width: position?.width,
        background: MENU_SPECS.standard.background,
        border: MENU_SPECS.standard.border,
        borderRadius: MENU_SPECS.standard.radius,
        boxShadow: MENU_SPECS.standard.shadow,
        padding: MENU_SPECS.standard.padding,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        maxHeight: position?.maxHeight ?? MENU_MAX_HEIGHT_PX,
        fontSize: CONTROL_TEXT_SIZE[size],
        visibility: position ? 'visible' : 'hidden',
        opacity: position ? 1 : 0,
    };

    // Form wrapper only when form-specific props are provided (backwards compatible).
    // `rightSideContent` renders inside it, so omitting it here would drop the link silently.
    const hasFormWrapper = !!(
        description ||
        tooltip ||
        hint ||
        rightSideContent ||
        name ||
        formControl ||
        required
    );

    // Only the form wrapper renders a <label>, so aria-labelledby has a target only when it
    // is present; without it the trigger falls back to aria-label or it would have no name.
    const hasLabelElement = hasFormWrapper && !!label;

    const coreComponent = (
        <>
            <div className="relative" style={controlWidthStyle(width)}>
                {/* Nesting <input> inside <button> is invalid HTML — use div[role=combobox] for inline search */}
                <TriggerElement
                    ref={triggerRef}
                    id={controlId}
                    triggerRole={
                        inlineSearch ? 'combobox' : chips && chipsRemovable ? 'group' : 'button'
                    }
                    disabled={disabled || loading}
                    {...{ [DROPDOWN_TRIGGER_ATTR]: '' }}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-controls={open ? listboxId : undefined}
                    aria-activedescendant={
                        inlineSearch && open && highlightedIndex >= 0
                            ? getOptionId(highlightedIndex)
                            : undefined
                    }
                    // aria-labelledby outranks aria-label in the accessible-name algorithm, so
                    // the generated one has to be dropped for a caller's aria-label to apply.
                    aria-labelledby={
                        ariaLabelledBy ?? (hasLabelElement && !ariaLabel ? labelId : undefined)
                    }
                    aria-label={ariaLabel ?? (hasLabelElement ? undefined : label)}
                    aria-invalid={error || undefined}
                    aria-required={required || undefined}
                    aria-describedby={describedBy}
                    aria-busy={loading || undefined}
                    onClick={() => {
                        if (!(disabled || loading)) setOpen((v) => !v);
                    }}
                    onKeyDown={handleOptionNavigation}
                    style={{
                        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                        ...triggerVisual,
                        ...style,
                    }}
                    className={`w-full ${TRIGGER_SIZE_CLASSES[size]} border border-solid flex items-center font-normal text-[var(--malachite-text-primary)] ${disabled || loading ? 'cursor-not-allowed' : 'cursor-pointer'} ${className}`}
                >
                    <span
                        className={`flex-1 min-w-0 text-left ${inlineSearch ? 'flex' : 'truncate'} ${clearable ? 'pr-[18px]' : ''}`}
                    >
                        {triggerLabel}
                    </span>
                    {loading ? (
                        <span className="flex items-center justify-center shrink-0 size-[14px]">
                            <Spinner size="xxxsmall" />
                        </span>
                    ) : (
                        <CaretDownIcon
                            size={13}
                            className="shrink-0"
                            style={{
                                color: open
                                    ? 'var(--malachite-text-primary, rgb(10, 10, 10))'
                                    : 'var(--malachite-text-placeholder, rgb(187, 187, 187))',
                                transition: 'transform 0.2s ease, color 0.2s ease',
                                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                        />
                    )}
                </TriggerElement>
                {clearable && (
                    <button
                        type="button"
                        aria-label="Clear selection"
                        onClick={clearAll}
                        className="absolute top-0 bottom-0 right-[28px] flex items-center justify-center px-[2px] leading-none bg-transparent border-none text-[var(--malachite-text-placeholder)] hover:text-[var(--malachite-text-primary)] cursor-pointer"
                    >
                        ×
                    </button>
                )}
            </div>
            {open &&
                createPortal(
                    <div
                        ref={popoverRef}
                        id={listboxId}
                        role="listbox"
                        aria-multiselectable="true"
                        {...overlayIdentity('multiselect')}
                        style={popoverStyle}
                    >
                        {/* Inline styles, not Tailwind: this portals into document.body, outside the
                            Shadow DOM the malachite stylesheet is adopted into. */}
                        {showSearch && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    background: 'var(--malachite-tag-default-background, #f1f1f1)',
                                    borderRadius: 8,
                                    height: 32,
                                    flexShrink: 0,
                                    padding: '0 12px',
                                }}
                            >
                                <SearchIcon
                                    size={13}
                                    style={{
                                        color: 'var(--malachite-text-placeholder, rgb(187, 187, 187))',
                                        flexShrink: 0,
                                    }}
                                />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                    onKeyDown={handleOptionNavigation}
                                    placeholder="Search"
                                    aria-label="Search options"
                                    autoFocus
                                    style={{
                                        flex: 1,
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        font: 'inherit',
                                        color: 'var(--malachite-text-primary, rgb(10, 10, 10))',
                                        minWidth: 0,
                                    }}
                                />
                            </div>
                        )}
                        {showSearch && (
                            <div
                                aria-hidden="true"
                                style={{
                                    height: 1,
                                    width: '100%',
                                    background: 'var(--malachite-card-border, rgb(232, 232, 232))',
                                    flexShrink: 0,
                                }}
                            />
                        )}
                        <div
                            ref={listRef}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                overflowY: 'auto',
                                overscrollBehavior: 'contain',
                                minHeight: 0,
                                flex: 1,
                                scrollbarWidth: 'thin',
                                scrollbarColor:
                                    'var(--malachite-text-placeholder, rgb(187, 187, 187)) transparent',
                            }}
                        >
                            {filteredOptions.length === 0 ? (
                                // `role="listbox"` admits only option/group children.
                                <div role="presentation" style={EMPTY_MESSAGE_STYLE}>
                                    {emptyMessage}
                                </div>
                            ) : (
                                filteredOptions.map((option, index) => {
                                    const checked = selectedValues.has(option.value);
                                    const highlighted = index === highlightedIndex;
                                    const optLocked = !!option.disabled;
                                    return (
                                        <button
                                            key={option.value}
                                            id={getOptionId(index)}
                                            ref={(el) => {
                                                optionRefs.current[index] = el;
                                            }}
                                            type="button"
                                            role="option"
                                            aria-selected={checked}
                                            aria-disabled={optLocked || undefined}
                                            onClick={() => {
                                                toggle(option.value);
                                                clearSearch();
                                                inlineInputRef.current?.focus();
                                            }}
                                            // Exception to "no JS for styling": the highlight IS
                                            // the keyboard cursor, so a bare :hover rule would
                                            // light a second row the keyboard has left.
                                            onMouseEnter={() => setHighlightedIndex(index)}
                                            onMouseLeave={() => {
                                                if (highlightedIndex === index)
                                                    setHighlightedIndex(-1);
                                            }}
                                            className="malachite-multiselect-option"
                                            data-checked={checked}
                                            data-highlighted={highlighted}
                                            data-disabled={optLocked}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: '100%',
                                                // The list scrolls; unpinned, rows compress instead.
                                                flexShrink: 0,
                                                border: 'none',
                                                textAlign: 'left',
                                                borderRadius: 8,
                                                padding: '8px 10px',
                                                font: 'inherit',
                                                cursor: optLocked ? 'not-allowed' : 'pointer',
                                                opacity: optLocked ? 0.6 : 1,
                                                color: checked
                                                    ? 'var(--malachite-accent-foreground, rgb(2, 122, 66))'
                                                    : 'var(--malachite-text-primary, rgb(10, 10, 10))',
                                            }}
                                        >
                                            {(() => {
                                                const mode = option.description
                                                    ? (option.descriptionMode ?? 'icon')
                                                    : null;
                                                const isSubtitle = mode === 'subtitle';
                                                const labelEl = (
                                                    <span
                                                        style={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {option.label}
                                                    </span>
                                                );

                                                return (
                                                    <span
                                                        style={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            display: 'flex',
                                                            flexDirection: isSubtitle
                                                                ? 'column'
                                                                : 'row',
                                                            alignItems: isSubtitle
                                                                ? 'flex-start'
                                                                : 'center',
                                                            gap: isSubtitle ? 2 : 6,
                                                        }}
                                                    >
                                                        {mode === 'hover' ? (
                                                            <Tooltip
                                                                text={option.description}
                                                                delay={0.6}
                                                                maxWidth={420}
                                                            >
                                                                {labelEl}
                                                            </Tooltip>
                                                        ) : (
                                                            labelEl
                                                        )}

                                                        {mode === 'icon' && (
                                                            <Tooltip
                                                                text={option.description}
                                                                delay={0.6}
                                                                maxWidth={420}
                                                            >
                                                                <span
                                                                    style={{
                                                                        cursor: 'help',
                                                                        color: 'var(--malachite-text-placeholder, rgb(187, 187, 187))',
                                                                    }}
                                                                    aria-label="More information"
                                                                    onClick={(e) =>
                                                                        e.stopPropagation()
                                                                    }
                                                                >
                                                                    {option.descriptionIcon ?? (
                                                                        <InfoFilledIcon size={14} />
                                                                    )}
                                                                </span>
                                                            </Tooltip>
                                                        )}

                                                        {mode === 'subtitle' && (
                                                            <span
                                                                style={{
                                                                    fontSize: 12,
                                                                    color: 'var(--malachite-text-secondary, rgb(123, 123, 123))',
                                                                    whiteSpace: 'normal',
                                                                    lineHeight: 1.3,
                                                                    ...(option.descriptionMaxLines
                                                                        ? {
                                                                              display:
                                                                                  '-webkit-box',
                                                                              WebkitLineClamp:
                                                                                  option.descriptionMaxLines,
                                                                              WebkitBoxOrient:
                                                                                  'vertical' as const,
                                                                              overflow: 'hidden',
                                                                          }
                                                                        : {}),
                                                                }}
                                                            >
                                                                {option.description}
                                                            </span>
                                                        )}
                                                    </span>
                                                );
                                            })()}
                                            {option.badge && (
                                                <span
                                                    style={{
                                                        marginLeft: 'auto',
                                                        flexShrink: 0,
                                                        paddingLeft: 8,
                                                    }}
                                                >
                                                    {option.badge}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    );

    if (!hasFormWrapper) {
        return coreComponent;
    }

    return (
        <BaseFormComponent
            label={label}
            hint={hint}
            rightSideContent={rightSideContent}
            tooltip={tooltip}
            name={(name ?? '') as FieldPath<FieldValues>}
            required={required}
            requiredIndicator={requiredIndicator}
            loading={loading}
            formControl={formControl}
            description={description}
            controlId={controlId}
            labelId={labelId}
            descriptionId={descriptionId}
            hintId={hintId}
            loadingComponent={
                width === undefined ? (
                    <LoadingInput heightClass={TRIGGER_SIZE_HEIGHT[size]} />
                ) : (
                    <div style={controlWidthStyle(width)}>
                        <LoadingInput heightClass={TRIGGER_SIZE_HEIGHT[size]} />
                    </div>
                )
            }
        >
            {coreComponent}
        </BaseFormComponent>
    );
};

export default MultiSelect;
